import { useCallback, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { organizationDetailsSignal } from '../OrganizationsList.signal';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { handleProgramMembershipLinkClick, fetchOrganization } from '../OrganizationsList.helpers';
import { addHttpsToUrl } from 'libs/string';
import { createGoogleMapsLink } from 'utils/queryAddresses';
import userAccountSignal from '../../../../signals/UserAccount.signal';
import BusinessDetails from './BusinessDetails';
import $appSettings from 'signals/AppSettings.signal';
import api from '../../../../api/api';
import alertSignal from '../../../../signals/Alert.signal';
import loaderSignal from '../../../../signals/Loader.signal';

const OrganizationDetails = ({
  hideOrganizationName = false,
  showLinkToPrograms = true,
  secondaryHeaderButton = null,
}) => {
  const [isInviting, setIsInviting] = useState(false);
  const { selectedOrganization } = organizationDetailsSignal.value;
  const { APPLICATION_STATUS, PORTAL_TYPES } = $appSettings.value.constants;
  const { pathname } = useLocation();
  const history = useHistory();
  const portalType = userAccountSignal.value?.userData?.account?.portalType;
  const currentLenderId = userAccountSignal.value?.userData?.account?.lender?.id;

  const lenderHasApplicationForBusiness = portalType === PORTAL_TYPES.lender
    && currentLenderId
    && selectedOrganization?.applications?.some(
      (app) => app.fundingProgramMembership?.lenderId === currentLenderId
    );

  const showViewApplicationsButton = portalType !== PORTAL_TYPES.lender || lenderHasApplicationForBusiness;

  const showInviteLender =
    portalType === PORTAL_TYPES.edo &&
    selectedOrganization?.account?.portalType === PORTAL_TYPES.lender &&
    (selectedOrganization?.account?._count?.users ?? 0) === 0 &&
    !!selectedOrganization?.email?.trim();

  const handleInviteLender = useCallback(async () => {
    if (!showInviteLender || isInviting) return;
    setIsInviting(true);
    loaderSignal.update({ isContentLoading: true });
    try {
      await api.post({
        path: '/v2/userInvites',
        body: { toLenderId: selectedOrganization.id },
      });
      alertSignal.update({
        type: 'notification',
        variant: 'success',
        message: 'Invitation sent successfully.',
      });
      await fetchOrganization(selectedOrganization.id, '/lenders');
    } catch (error) {
      alertSignal.update({
        type: 'notification',
        variant: 'warning',
        message: error.message,
        error,
      });
    } finally {
      setIsInviting(false);
      loaderSignal.reset();
    }
  }, [showInviteLender, isInviting, selectedOrganization?.id]);

  if (selectedOrganization.sediCertification) {
    return (
      <BusinessDetails
        hideOrganizationName={hideOrganizationName}
        headerButton={showViewApplicationsButton ? {
          label: 'View Applications',
          onClick: () => {
            history.push(
              portalType === PORTAL_TYPES.lender
                ? `/lender/portfolio?businessId=${selectedOrganization.id}`
                : `/edo/portfolio?businessId=${selectedOrganization.id}`
            );
          },
          disabled: false,
        } : null}
        secondaryHeaderButton={secondaryHeaderButton}
      />
    );
  }

  return (
    <div className="pb-16">
      <div className="d-flex flex-row justify-content-between align-items-center">

        <h3 hidden={hideOrganizationName} className="mb-16">
          {selectedOrganization.name}
        </h3>
        <div className="d-flex">
          {!!secondaryHeaderButton && (
            <Button
              variant="outline-primary"
              className="rounded mr-2"
              disabled={secondaryHeaderButton.disabled}
              onClick={secondaryHeaderButton.onClick}
            >
              {secondaryHeaderButton.label}
            </Button>
          )}
          {showInviteLender && (
            <Button
              variant="outline-primary"
              className="rounded mr-2"
              disabled={isInviting}
              onClick={handleInviteLender}
            >
              {isInviting ? 'Sending...' : 'Invite Lender'}
            </Button>
          )}
          <Button
            className="rounded"
            onClick={() => {
              history.push(`/edo/portfolio?lenderId=${selectedOrganization.id}`);
            }}
          >
            View Applications
          </Button>
        </div>
      </div>
      <hr hidden={hideOrganizationName} />
      <Row>
        <Col>
          <p className="font-weight-bold mb-0">Contact Name</p>
          {selectedOrganization.contactName}
        </Col>
        <Col>
          <p className="font-weight-bold mb-0">Email</p>
          <a
            href={`mailto:${selectedOrganization.email || selectedOrganization.contactEmail
              }`}
          >
            {selectedOrganization.email || selectedOrganization.contactEmail}
          </a>
        </Col>
        <Col>
          <p className="font-weight-bold mb-0">Phone Number</p>
          <a href={`tel:${selectedOrganization.phoneNumber}`}>
            {selectedOrganization.phoneNumber}
          </a>
        </Col>
        <Col xs={3} className="text-overflow-ellipsis">
          <p className="font-weight-bold mb-0">Website</p>
          <a
            href={
              selectedOrganization?.website
                ? addHttpsToUrl(selectedOrganization.website)
                : ''
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            {selectedOrganization.website}
          </a>
        </Col>
      </Row>
      <Row className="mt-40">
        <Col xs={3}>
          <p className="font-weight-bold mb-0">Address</p>
          <p className="mb-0">
            <a
              href={
                selectedOrganization.address?.formattedAddress
                  ? createGoogleMapsLink(
                    selectedOrganization.address?.formattedAddress
                  )
                  : null
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              {selectedOrganization.address?.formattedAddress}
            </a>
          </p>
        </Col>
      </Row>
      <Row className="mt-40">
        <Col>
          <p className="font-weight-bold mb-0">Description</p>
          {selectedOrganization.description}
        </Col>
      </Row>
      {window.location.hostname !== 'app.caat.io' &&
        <Row
          className="mt-40"
          hidden={!selectedOrganization.fundingProgramMemberships}
        >
          <Col xs={3}>
            <p className="font-weight-bold mb-0">Program Membership(s)</p>
            {selectedOrganization.fundingProgramMemberships?.map((fpm) => {
              let url;
              if (
                userAccountSignal.value.userData.account.portalType ===
                PORTAL_TYPES.edo
              ) {
                url = `${pathname}/program-membership/${fpm.id}`;
              } else if (
                userAccountSignal.value.userData.account.portalType ===
                PORTAL_TYPES.business
              ) {
                url = `/business/funding-programs/${fpm.fundingProgramId}`;
              }

              return (
                <div key={fpm.id}>
                  {showLinkToPrograms ? (
                    <Link
                      to={url}
                      className="mb-0"
                      onClick={() =>
                        handleProgramMembershipLinkClick(fpm, url, pathname)
                      }
                    >
                      {fpm.fundingProgram.name}
                    </Link>
                  ) : (
                    <p className="mb-0">{fpm.fundingProgram.name}</p>
                  )}
                </div>
              );
            })}
          </Col>
          <Col xs={3}>
            <p className="font-weight-bold mb-0">Pending Program Membership(s)</p>
            {selectedOrganization.programRequests
              ?.filter(
                (pr) =>
                  pr.status !== APPLICATION_STATUS.approved &&
                  pr.status !== APPLICATION_STATUS.denied
              )
              .map((pr) => (
                <p key={pr.id} className="mb-0">
                  {pr.fundingProgram.name}
                </p>
              ))}
          </Col>
          <Col
            hidden={
              userAccountSignal.value.userData.account.portalType !==
              PORTAL_TYPES.business ||
              selectedOrganization?.account?.portalType !==
              PORTAL_TYPES.vc
            }
          >
            <p className="font-weight-bold mb-0">Funds</p>
            {selectedOrganization.fundingProgramMemberships?.map((fpm) => (
              <div key={fpm.id}>
                {fpm.funds.map((fund) => (
                  <div key={fund.id}>
                    {showLinkToPrograms ? (
                      <Link
                        to={`${pathname}/funds/${fund.id}`}
                        className="mb-0"
                      >
                        {fund.fundName}
                      </Link>
                    ) : (
                      <p className="mb-0">{fund.fundName}</p>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </Col>
        </Row>
      }
    </div>
  );
};

export default OrganizationDetails;
